import {
  Check,
  Construction,
  Delete,
  Download,
  Edit,
  Error,
  Help,
  Key,
  MoreVert,
  Refresh,
  Search,
  Sync,
  TravelExploreOutlined,
  Visibility,
  Warning,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useEffect, useState, useRef } from "react";
import {
  removeIntegration as apiRemoveIntegration,
  syncIntegration as apiSyncIntegration,
  fetchCompanies,
  fetchIntegrations,
} from "./api";
// Removed inline CopyBlock usage; now using APIKeyDialog
import { Link as DomLink } from "react-router-dom";
import capture from "../../capture";
import {
  DEFAULT_MODEL,
  INTEGRATION_SHARING_SHARED,
  INTEGRATION_SHARING_PRIVATE,
} from "../../constants";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { Company } from "../../types";
import { formatTimestamp, getApiDomain, parseError } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";
import APIKeyDialog from "./APIKeyDialog";
import {
  createApiKey,
  deleteApiKey as deleteApiKeyApi,
  fetchApiKeys,
  getApiKeyByName,
} from "./apiKeys";
import {
  EDITABLE_INTEGRATIONS,
  INTEGRATION_LOGOS,
  KNOWLEDGE_BASE_INTEGRATIONS,
  OAUTH_INTEGRATIONS,
} from "./constants";
import { INTEGRATION_PROVIDER_COMPOSIO } from "../../constants";
import CreateKeyDialog from "./CreateKeyDialog";
import DeleteIntegrationDialog from "./DeleteIntegrationDialog";
import { getApiKey } from "./installers";
import InstallIntegrationDialog from "./InstallIntegrationDialog";
import ChatSettingsDialog from "../chat/ChatSettingsDialog";
import McpSetupSection from "../../components/McpSetupSection";
import BrowserSessionDialog from "./dialogs/BrowserSessionDialog";
import BrowseToolsBanner from "./components/BrowseToolsBanner";
import ComposioCatalogDialog from "./dialogs/ComposioCatalogDialog";
import IntegrationSharingDialog from "./dialogs/IntegrationSharingDialog";
import ToolsetsManagementDialog from "./dialogs/ToolsetsManagementDialog";

export default function ({
  currentUser,
  company,
  companyId,
  token,
  getToken,
  eventListener,
  employees,
  currentPlan,
}: {
  currentUser: User;
  company: any;
  companyId: string;
  token: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  employees: Array<any>;
  currentPlan: string;
}) {
  const { employeeLower, employeesLower, employees: employeesLabel } = useEmployeeTerminology();
  const [init, setInit] = useState(false);
  const [integrationData, setIntegrationsData] = useState<Array<any>>([]);
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [openApiKeyDialog, setOpenApiKeyDialog] = useState(false);
  const [apiKeys, setApiKeys] = useState<Array<any>>([]);
  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIntegrationRow, setSelectedIntegrationRow] =
    useState<any>(null);
  const [apiKeyMenuAnchorEl, setApiKeyMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<any>(null);
  const [refreshingIntegrations, setRefreshingIntegrations] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingIntegration, setDeletingIntegration] = useState(false);
  const [browserSessionDialogOpen, setBrowserSessionDialogOpen] = useState(false);
  const [selectedBrowserIntegration, setSelectedBrowserIntegration] = useState<any>(null);
  const [composioCatalogOpen, setComposioCatalogOpen] = useState(false);
  const [sharingDialogOpen, setSharingDialogOpen] = useState(false);
  const [sharingIntegration, setSharingIntegration] = useState<any>(null);
  const [toolsetsDialogOpen, setToolsetsDialogOpen] = useState(false);

  // Sync polling state
  const [syncingIntegrationId, setSyncingIntegrationId] = useState<string | null>(null);
  const syncPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const seenRunningRef = useRef<boolean>(false);
  // Guards the ?reauthDone=<id> handler so it only confirms re-auth once per load.
  const reauthHandledRef = useRef<boolean>(false);

  // Pagination and search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { whitelabelSettings } = useWhitelabel();

  // Helper function to get the logo for an integration
  // Handles native integrations (via INTEGRATION_LOGOS) and Composio integrations (via parameters or generated URL)
  const getIntegrationLogo = (row: any): string | undefined => {
    // First check native integration logos
    if (INTEGRATION_LOGOS[row.type]) {
      return INTEGRATION_LOGOS[row.type];
    }

    // Check stored logo in parameters
    if (row.parameters?.logo) {
      return row.parameters.logo;
    }

    // For Composio integrations without a stored logo, generate the URL
    if (row.provider === INTEGRATION_PROVIDER_COMPOSIO) {
      // Use composioSlug, composioAppSlug, or fall back to type
      const slug = row.parameters?.composioSlug || row.parameters?.composioAppSlug || row.type;
      if (slug) {
        // Normalize the slug: remove _tool suffix and convert to lowercase
        const normalizedSlug = slug.toLowerCase().replace(/_tool$/, '');
        return `https://logos.composio.dev/api/${normalizedSlug}`;
      }
    }

    // Toolsets use Construction icon as fallback (handled in Avatar children)
    return undefined;
  };

  // Helper function to determine if an integration should be shown
  const shouldShowIntegration = (integrationType: string) => {
    // If we're on a whitelabel domain (not the main site), hide OAuth integrations
    if (whitelabelSettings && !whitelabelSettings.isMainSite) {
      return !OAUTH_INTEGRATIONS.includes(integrationType);
    }
    // On main site, show all integrations
    return true;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedIntegrationRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedIntegrationRow(null);
  };

  const handleOpenBrowserSession = () => {
    setSelectedBrowserIntegration(selectedIntegrationRow);
    setBrowserSessionDialogOpen(true);
    handleMenuClose();
  };

  const handleOpenSharingDialog = () => {
    setSharingIntegration(selectedIntegrationRow);
    setSharingDialogOpen(true);
    handleMenuClose();
  };

  const handleSharingDialogClose = () => {
    setSharingDialogOpen(false);
    setSharingIntegration(null);
  };

  const handleSharingDialogSaved = () => {
    // Refresh integrations after saving sharing settings
    getIntegrations();
  };

  const handleSyncIntegration = () => {
    if (selectedIntegrationRow) {
      syncIntegration(
        selectedIntegrationRow.instanceName,
        selectedIntegrationRow.type,
        selectedIntegrationRow.companyId,
        selectedIntegrationRow.id
      );
    }
    handleMenuClose();
  };

  const handleRemoveIntegration = () => {
    setDeleteConfirmOpen(true);
    setMenuAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedIntegrationRow) {
      setDeletingIntegration(true);
      try {
        await removeIntegration(
          selectedIntegrationRow.id,
          selectedIntegrationRow.companyId
        );
        // Show success message
        eventListener({
          type: "SET_SUCCESS",
          message: `Integration "${
            selectedIntegrationRow.instanceName ||
            selectedIntegrationRow.name ||
            "Unknown Integration"
          }" has been successfully removed.`,
        });
      } catch (err: any) {
        console.error("Error removing integration:", err);
        eventListener({ type: "SET_ERROR", error: parseError(err) });
      } finally {
        setDeletingIntegration(false);
        setDeleteConfirmOpen(false);
        setSelectedIntegrationRow(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSelectedIntegrationRow(null);
  };

  const handleApiKeyMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    apiKey: any
  ) => {
    setApiKeyMenuAnchorEl(event.currentTarget);
    setSelectedApiKey(apiKey);
  };

  const handleApiKeyMenuClose = () => {
    setApiKeyMenuAnchorEl(null);
    setSelectedApiKey(null);
  };

  const handleShowApiKeyFromMenu = () => {
    if (selectedApiKey) {
      handleShowApiKey(selectedApiKey.name);
    }
    handleApiKeyMenuClose();
  };

  const handleDeleteApiKeyFromMenu = () => {
    if (selectedApiKey) {
      handleDeleteApiKey(selectedApiKey.name);
    }
    handleApiKeyMenuClose();
  };

  const handleRefreshIntegrations = async () => {
    setRefreshingIntegrations(true);
    try {
      setCurrentPage(1); // Reset to first page on refresh
      await getIntegrations();
    } finally {
      setRefreshingIntegrations(false);
    }
  };

  const handleEditIntegration = async () => {
    if (selectedIntegrationRow) {
      setEditingIntegration(selectedIntegrationRow);
      setSelectedIntegration(selectedIntegrationRow.type);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "healthy":
      case "connected":
      case "active":
        return <Check fontSize="small" />;
      case "running":
        return <CircularProgress size={18} />;
      case "syncing":
      case "warning":
      case "slow":
        return <Warning fontSize="small" />;
      case "error":
      case "failed":
      case "disconnected":
        return <Error fontSize="small" />;
      default:
        return <Help fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "healthy":
      case "connected":
      case "active":
        return "primary";
      case "warning":
      case "slow":
        return "warning";
      case "error":
      case "failed":
      case "disconnected":
        return "error";
      default:
        return "default";
    }
  };

  const formatSyncFrequency = (frequency: string) => {
    if (!frequency) return "-";
    switch (frequency.toLowerCase()) {
      case "daily":
        return "Daily";
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      case "hourly":
        return "Hourly";
      case "never":
        return "Manual";
      default:
        return frequency.charAt(0).toUpperCase() + frequency.slice(1);
    }
  };

  const isKnowledgeBaseIntegration = (type: string) => {
    return KNOWLEDGE_BASE_INTEGRATIONS.includes(type);
  };

  const handleGetApiKey = async () => {
    try {
      const key = await getApiKey(selectedIntegration, companyId, getToken);
      setApiKey(key);
      setOpenApiKeyDialog(true);
      capture("Installed integration", { type: selectedIntegration });
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const getCompanies = async () => {
    try {
      const comps = await fetchCompanies(getToken);
      setCompanies(comps);
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const getIntegrations = async () => {
    try {
      const data = await fetchIntegrations(
        companyId,
        getToken,
        currentPage,
        pageSize,
        debouncedSearchQuery
      );
      console.log("[Sync] Fetched integrations, statuses:", data.integrations?.map((i: any) => ({ name: i.instanceName, status: i.status })));
      setIntegrationsData(data.integrations || []);
      if (data.pagination) {
        setTotalCount(data.pagination.totalCount || 0);
        setTotalPages(data.pagination.totalPages || 1);
      }
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setLoading(false);
    }
  };

  const removeIntegration = async (
    integrationId: string,
    companyId: string
  ) => {
    await apiRemoveIntegration(integrationId, companyId, getToken);
    // Refresh integrations list after successful removal
    await getIntegrations();
  };

  const syncIntegration = async (
    instanceName: string,
    type: string,
    companyId: string,
    integrationId: string
  ) => {
    try {
      console.log("[Sync] Starting sync for:", { instanceName, type, companyId, integrationId });
      await apiSyncIntegration(instanceName, type, companyId, getToken);
      console.log("[Sync] API call completed, setting up polling");

      // Reset the "seen running" flag and set the syncing integration ID
      seenRunningRef.current = false;
      setSyncingIntegrationId(integrationId);

      // Clear any existing poll interval
      if (syncPollIntervalRef.current) {
        clearInterval(syncPollIntervalRef.current);
      }

      // Start polling every 4 seconds (first poll after 2 seconds to allow DB update)
      syncPollIntervalRef.current = setInterval(() => {
        console.log("[Sync] Polling for updates...");
        getIntegrations();
      }, 4000);

      // Initial refresh after a short delay to allow backend to update status
      setTimeout(() => {
        console.log("[Sync] Initial refresh (delayed)");
        getIntegrations();
      }, 2000);
    } catch (err: any) {
      console.error("[Sync] Error:", err);
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const integrationInstallStyle = (type: string) => {
    if (type === "google_docs") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("google_docs");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "slack") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("slack");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "atlassian") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("atlassian");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "notion") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("notion");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "discord") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("discord");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "zapier") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("zapier");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "chrome") {
      return (
        <React.Fragment>
          <Button
            type="button"
            variant="outlined"
            component={Link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://chromewebstore.google.com/detail/gtm-ai/ghfjnjjpepjibmkegmafnlammfopdifk"
            style={{ marginTop: 20, marginRight: 10 }}
          >
            Install
          </Button>
          <Button
            type="button"
            variant="outlined"
            style={{ marginTop: 20 }}
            onClick={() => {
              setSelectedIntegration("chrome");
              setDialogOpen(true);
            }}
          >
            Get API Key
          </Button>
        </React.Fragment>
      );
    } else if (type === "wordpress") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("wordpress");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "linkedin") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("linkedin");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "linkedin_login") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("linkedin_login");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "aws") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("aws");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "sendgrid") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("sendgrid");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "website") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("website");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "heygen") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("heygen");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "twilio") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("twilio");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "imap") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("imap");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "office365") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("office365");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "mcp") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("mcp");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "browser_session") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("browser_session");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "shopify") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("shopify");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "webflow") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("webflow");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "scheduling") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("scheduling");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "hubspot") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("hubspot");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "gohighlevel") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("gohighlevel");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "salesforce") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("salesforce");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "facebook") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("facebook");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "instagram") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("instagram");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "apollo") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("apollo");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "findymail") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("findymail");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "bettercontact") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("bettercontact");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "prosp") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("prosp");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "salesforge") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("salesforge");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "heyreach") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("heyreach");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "thanksio") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("thanksio");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "google_analytics") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("google_analytics");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "meta_ads") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("meta_ads");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else if (type === "gmail") {
      return (
        <Button
          type="button"
          variant="outlined"
          style={{ marginTop: 20 }}
          onClick={() => {
            setSelectedIntegration("gmail");
            setDialogOpen(true);
          }}
        >
          Install
        </Button>
      );
    } else {
      return (
        <Button type="button" variant="outlined" style={{ marginTop: 20 }}>
          Coming soon
        </Button>
      );
    }
  };

  // API keys management helpers
  const loadApiKeys = async () => {
    try {
      const keys = await fetchApiKeys(companyId, getToken);
      setApiKeys(keys);
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleCreateApiKey = async (name: string) => {
    setCreatingKey(true);
    try {
      const key = await createApiKey(companyId, name, getToken);
      setApiKey(key);
      setOpenApiKeyDialog(true);
      setCreateKeyDialogOpen(false);
      loadApiKeys();
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setCreatingKey(false);
    }
  };

  const handleDeleteApiKey = async (name: string) => {
    try {
      await deleteApiKeyApi(companyId, name, getToken);
      loadApiKeys();
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleShowApiKey = async (name: string) => {
    try {
      const key = await getApiKeyByName(companyId, name, getToken);
      setApiKey(key);
      setOpenApiKeyDialog(true);
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Fetch integrations when dependencies change
  useEffect(() => {
    if (currentUser.uid) {
      getIntegrations();
    }
  }, [currentUser.uid, companyId, currentPage, debouncedSearchQuery]);

  // Initialize companies and API keys
  useEffect(() => {
    if (currentUser.uid) {
      setInit(true);
      getCompanies();
      loadApiKeys();
    }
  }, [currentUser.uid, companyId]);

  // Handle the ?reauthDone=<integrationId> deeplink. The "connection expired"
  // notification sends users through Composio's OAuth refresh and back here. We
  // confirm the connection is active again and restore the integration to healthy
  // so its mailbox resumes syncing immediately, then clear the param.
  useEffect(() => {
    if (!currentUser.uid || reauthHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const reauthIntegrationId = params.get("reauthDone");
    if (!reauthIntegrationId) return;
    reauthHandledRef.current = true;

    const confirmReauth = async () => {
      try {
        const token = await getToken();
        const response = await axios.post(
          `${getApiDomain()}/integrations/composio/confirm-reauth`,
          { integrationId: reauthIntegrationId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.recovered) {
          eventListener({
            type: "SET_SUCCESS",
            message: "Connection restored. Your inbox is syncing again.",
          });
          getIntegrations();
        } else if (response.data?.active) {
          // Connection is active but the integration wasn't in a failed state.
          eventListener({
            type: "SET_SUCCESS",
            message: "Connection is active.",
          });
        } else {
          eventListener({
            type: "SET_ERROR",
            error:
              "The connection isn't active yet. Please try reconnecting again.",
          });
        }
      } catch (err: any) {
        eventListener({ type: "SET_ERROR", error: parseError(err) });
      } finally {
        // Strip our param and the params Composio appends on redirect
        // (status, connectedAccountId, appName) so a refresh doesn't re-trigger
        // the confirmation and the URL stays clean.
        ["reauthDone", "status", "connectedAccountId", "appName"].forEach((p) =>
          params.delete(p)
        );
        const newSearch = params.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`
        );
      }
    };

    confirmReauth();
  }, [currentUser.uid]);

  // Stop polling when syncing integration is no longer in "running" state
  useEffect(() => {
    if (syncingIntegrationId) {
      const syncingIntegration = integrationData.find(
        (i) => i.id === syncingIntegrationId
      );
      console.log("[Sync] Checking poll stop:", {
        syncingIntegrationId,
        found: !!syncingIntegration,
        status: syncingIntegration?.status,
        seenRunning: seenRunningRef.current
      });

      // Track when we first see "running" status
      if (syncingIntegration?.status === "running") {
        seenRunningRef.current = true;
      }

      // Only stop polling if we've seen "running" and status changed to something else
      if (seenRunningRef.current && syncingIntegration && syncingIntegration.status !== "running") {
        console.log("[Sync] Stopping polling - status changed to:", syncingIntegration.status);
        if (syncPollIntervalRef.current) {
          clearInterval(syncPollIntervalRef.current);
          syncPollIntervalRef.current = null;
        }
        setSyncingIntegrationId(null);
        seenRunningRef.current = false;
      }
    }
  }, [integrationData, syncingIntegrationId]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (syncPollIntervalRef.current) {
        clearInterval(syncPollIntervalRef.current);
      }
    };
  }, []);

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <Box>
      <APIKeyDialog
        open={openApiKeyDialog}
        onClose={() => setOpenApiKeyDialog(false)}
        apiKey={apiKey}
      />
      <CreateKeyDialog
        open={createKeyDialogOpen}
        onClose={() => setCreateKeyDialogOpen(false)}
        onCreate={handleCreateApiKey}
        loading={creatingKey}
      />
      <InstallIntegrationDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingIntegration(null);
        }}
        selectedIntegration={selectedIntegration}
        getToken={getToken}
        eventListener={eventListener}
        company={company}
        companyId={companyId}
        editMode={!!editingIntegration}
        editingIntegrationId={editingIntegration?.id}
        onSaveSuccess={() => {
          getIntegrations(); // Refresh the integrations list
        }}
      />
      <ComposioCatalogDialog
        open={composioCatalogOpen}
        onClose={() => setComposioCatalogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        onSuccess={() => {
          getIntegrations();
          eventListener({
            type: "SET_SUCCESS",
            message: "Integration installed successfully",
          });
        }}
        onError={(error) => eventListener({ type: "SET_ERROR", error })}
      />
      <Box sx={{ mt: 2 }}>
        <BrowseToolsBanner onClick={() => setComposioCatalogOpen(true)} />
      </Box>

      {/* Employee Tools Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {employeesLabel} Tools
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Give AI {employeesLower} access to specialized tools and capabilities. These integrations extend what {employeesLower} can do - from browser automation to custom tool servers.
        </Typography>
      </Box>
      <Grid container spacing={2} data-tutorial="available-integrations">
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["mcp"]}
                  alt="MCP Tool"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                MCP Tool
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect external MCP servers to agents
                <br />
                • Access custom tools and capabilities
                <br />• HTTP protocol for secure cloud connections
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("mcp")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["browser_session"]}
                  alt="Browser Session"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Browser Session
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Save login sessions for any website
                <br />
                • Secure browser-based authentication
                <br />• Use saved sessions in automation workflows
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("browser_session")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    marginLeft: 1.25,
                    bgcolor: "background.paper",
                    color: "text.primary",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Construction />
                </Avatar>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Toolsets
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Create curated collections of AI tools
                <br />
                • Pick specific functions for AI {employeesLower}
                <br />• Streamline tool access for different use cases
              </Typography>
            </div>
            <div>
              <Button
                type="button"
                variant="outlined"
                style={{ marginTop: 20 }}
                onClick={() => setToolsetsDialogOpen(true)}
              >
                Manage
              </Button>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Knowledge Base Sync Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Knowledge Base Sync
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Automatically sync documents from your existing tools to train AI {employeesLower}. Content is indexed and kept up-to-date so {employeesLower} always have access to the latest information.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 40, marginLeft: 8, marginTop: 5 }}
                  src={require("../../img/gdrive.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Google Drive
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync to knowledge base for training and context
                <br />
                • Auto-import Google Docs and Sheets
                <br />• Keep AI {employeesLower} updated with latest files
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("google_docs")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 51 }}
                  src={require("../../img/confluence.svg")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Confluence
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync to knowledge base for training and context
                <br />
                • Import company wiki and documentation
                <br />• Train AI on internal processes and procedures
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("atlassian")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 39, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/notion.svg")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Notion
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync to knowledge base for training and context
                <br />
                • Connect team workspaces and databases
                <br />• Import project docs and meeting notes
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("notion")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Automation & Workflows Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Automation & Workflows
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect AI {employeesLower} to workflow automation platforms. Trigger {employeeLower} actions from external events or integrate {employeeLower} responses into your existing workflows.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 76, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/zapier.svg")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Zapier
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect to 5000+ business applications
                <br />
                • Automate workflows with AI {employeesLower}
                <br />• Trigger actions from CRM, email, and more
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("zapier")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Social Media & Publishing Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Social Media & Publishing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Publish content from Content Engine to social platforms and websites. Schedule and automate posts across LinkedIn, Facebook, Instagram, WordPress, Shopify, and more.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {/* Chrome Extension integration temporarily disabled */}
        {/* <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <Box style={{ height: 60 }}>
              <img
                style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                src={require("../../img/chrome.svg")}
              ></img>
            </Box>
            <Typography variant="h6" style={{ marginLeft: 5 }}>
              Chrome Extension (Beta)
            </Typography>
            <Typography variant="body2" style={{ marginLeft: 5 }}>
              <VerifiedIcon style={{ marginRight: 6, fontSize: 12 }} />
              Built by GTM AI
            </Typography>
            {integrationInstallStyle("chrome")}
          </Paper>
        </Grid> */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/wordpress_logo.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Wordpress
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Publish content-engine content to your website
                <br />
                • Auto-generate blog posts from knowledge base
                <br />• Maintain consistent brand voice
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("wordpress")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/linkedin.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                LinkedIn Personal
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Auto-publish content to your LinkedIn profile
                <br />
                • Schedule and manage professional posts
                <br />• Expand your professional network reach
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("linkedin")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["facebook"]}
                  alt="Facebook"
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Facebook
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect with one click - no app setup required
                <br />
                • Post to your Facebook pages
                <br />• Manage your Facebook presence
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("facebook")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["instagram"]}
                  alt="Instagram"
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Instagram
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect with one click - no app setup required
                <br />
                • Post to your Instagram account
                <br />• Manage your Instagram presence
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("instagram")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["shopify"]}
                  alt="Shopify"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Shopify
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Publish content-engine blog posts to your store
                <br />
                • Auto-publish articles to your store blog
                <br />• Maintain consistent brand voice across content
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("shopify")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["webflow"] || `https://logos.composio.dev/api/webflow`}
                  alt="Webflow"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Webflow
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Publish content directly to your Webflow CMS
                <br />
                • Auto-publish blog posts to your site
                <br />• Maintain consistent brand voice across content
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("webflow")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["google_analytics"]}
                  alt="Google Analytics"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Google Analytics
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Track blog traffic for published content
                <br />
                • Pulls page views and sessions from GA4
                <br />• Works with any web destination (WordPress, Webflow, etc.)
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("google_analytics")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Website Crawler - Knowledge Base Addition */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Web Content Import
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Extract and import content from websites directly into your knowledge base. Useful for competitor research, industry data, and external documentation.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <TravelExploreOutlined sx={{ height: 40, width: 40 }} />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Website Crawler
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Extract content from any website
                <br />
                • Save website data to your knowledge base
                <br />• Import competitor research and industry data
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("website")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Outreach & Communication Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Outreach & Communication
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send emails, SMS messages, and create personalized videos. These integrations are used by sequences and agents for outreach, automated replies, and conversational messaging.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 48, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/aws.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                AWS (SES)
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Send emails with the sequences feature
                <br />
                • Enterprise-grade email delivery
                <br />• Reliable AWS infrastructure
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("aws")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 125, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/sendgrid.svg")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                SendGrid
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Send emails with the sequences feature
                <br />
                • Professional email delivery service
                <br />• Advanced analytics and delivery tracking
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("sendgrid")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 40, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/mail-blue.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                IMAP Email
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect your email account for inbox management
                <br />
                • Receive and reply to emails through AI {employeesLower}
                <br />• Support for Gmail, Outlook, and other email providers
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("imap")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 40, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["office365"]}
                  alt="Office 365"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Office 365 Email
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect your Office 365 email with OAuth
                <br />
                • Secure access without passwords using MS OAuth
                <br />• Full inbox management and email sending capabilities
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("office365")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 40, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["gmail"]}
                  alt="Gmail"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Gmail
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Connect your Gmail with secure Google OAuth
                <br />
                • Full inbox management and email sending
                <br />• Works with personal and Workspace accounts
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("gmail")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/heygen-square.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                HeyGen
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Generate AI videos from templates
                <br />
                • Create personalized video content at scale
                <br />• Integrate with smart lists for video automation
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("heygen")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <img
                  style={{ width: 80, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/twilio.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Twilio
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Send automated SMS sequences to contacts
                <br />
                • Deploy SMS and voice AI agents
                <br />• Enable two-way conversational messaging
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("twilio")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={require("../../img/linkedin.png")}
                ></img>
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                LinkedIn Login
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Browser login for LinkedIn automation
                <br />
                • Connect, message, and post on LinkedIn
                <br />• Capture session cookies for automation
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("linkedin_login")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["prosp"]}
                  alt="Prosp"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Prosp
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Add leads to Prosp campaigns from sequences
                <br />
                • Sync contact data with LinkedIn URLs
                <br />• Automate multi-channel outreach workflows
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("prosp")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["heyreach"]}
                  alt="HeyReach"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                HeyReach
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Add leads to HeyReach campaigns from sequences
                <br />
                • Sync contact data with LinkedIn URLs
                <br />• Automate LinkedIn outreach at scale
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("heyreach")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["salesforge"]}
                  alt="Salesforge"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Salesforge
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Add contacts to Salesforge multichannel sequences
                <br />
                • Email + LinkedIn outreach from one platform
                <br />• Sync prospects with personalized campaigns
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("salesforge")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["thanksio"]}
                  alt="Thanks.io"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Thanks.io
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Send personalized direct mail in sequences
                <br />
                • Postcards, letters, and notecards
                <br />• Handwritten messages at scale
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("thanksio")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} variant="outlined">
            <div>
              <Box style={{ height: 60 }}>
                <img
                  style={{ width: 38, marginTop: 8, marginLeft: 6 }}
                  src={INTEGRATION_LOGOS["meta_ads"]}
                  alt="Meta Ads"
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Meta Ads
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Add sequence members to custom audiences
                <br />
                • Target leads on Facebook & Instagram
                <br />• Retarget engaged prospects with ads
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("meta_ads")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* CRM & Sales Tools Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          CRM & Sales Tools
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect AI {employeesLower} to your CRM and sales tools. Sync contacts, log activities, enrich lead data, and track meeting bookings automatically.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["scheduling"]}
                  alt="Calendar Scheduling"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Calendar Scheduling
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Track meeting bookings from sequences
                <br />
                • Works with MeetQuick, Calendly, and more
                <br />• See booking events in sequence timeline
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("scheduling")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["hubspot"]}
                  alt="HubSpot"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                HubSpot
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync contacts from sequence replies
                <br />
                • Log activities when leads respond
                <br />• Keep your CRM in sync automatically
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("hubspot")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["gohighlevel"]}
                  alt="GoHighLevel"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                GoHighLevel
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync contacts from sequence replies
                <br />
                • Log activities when leads respond
                <br />• Keep your CRM in sync automatically
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("gohighlevel")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["salesforce"]}
                  alt="Salesforce"
                  sx={{ width: 50, height: 50, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Salesforce
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Sync contacts from sequence replies
                <br />
                • Log activities when leads respond
                <br />• Keep your CRM in sync automatically
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("salesforce")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["apollo"]}
                  alt="Apollo"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Apollo
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Enrich contacts with Apollo data
                <br />
                • Search for leads and prospects
                <br />• Sync Apollo data with your lists
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("apollo")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["findymail"]}
                  alt="Findymail"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Findymail
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Find verified email addresses
                <br />
                • Enrich leads with contact data
                <br />• Search by name, domain, or LinkedIn
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("findymail")}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" style={{ padding: 10, height: 260, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Box style={{ height: 60, display: "flex", alignItems: "center" }}>
                <Avatar
                  src={INTEGRATION_LOGOS["bettercontact"]}
                  alt="Better Contact"
                  sx={{ width: 40, height: 40, marginLeft: 1.25 }}
                />
              </Box>
              <Typography variant="h6" style={{ marginLeft: 5 }}>
                Better Contact
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 5, marginTop: 8, color: "text.secondary", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                • Waterfall enrichment with 20+ providers
                <br />
                • Find verified emails and phone numbers
                <br />• High match rates for B2B contacts
              </Typography>
            </div>
            <div>
              {integrationInstallStyle("bettercontact")}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* API Keys management section */}
      <div>
        <Typography variant="h5" style={{ marginTop: 40 }}>
          API Keys
        </Typography>
        <Button
          variant="contained"
          style={{ marginTop: 20, marginBottom: 10 }}
          onClick={() => setCreateKeyDialogOpen(true)}
          startIcon={<Key />}
        >
          New API Key
        </Button>
        <Typography variant="body2" style={{ marginTop: 10 }}>
          Click <a href={`${getApiDomain()}/docs`} target="_blank" rel="noopener noreferrer">here</a> for api
          documentation
        </Typography>
        <TableContainer
          component={Paper}
          variant="outlined"
          style={{ marginTop: 10, marginBottom: 40 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key.name}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    {key.apiKey
                      ? "*".repeat(Math.max(0, key.apiKey.length - 4)) +
                        key.apiKey.slice(-4)
                      : ""}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="more"
                      aria-controls={`api-key-menu-${key.name}`}
                      aria-haspopup="true"
                      onClick={(event) => handleApiKeyMenuOpen(event, key)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <McpSetupSection variant="apiKey" />
      </div>
      {(integrationData.length !== 0 || searchQuery.trim() !== "") && (
        <div>
          <Typography variant="h5" style={{ marginTop: 40 }}>
            Installed
          </Typography>
          <Paper
            component={Paper}
            variant="outlined"
            style={{ marginTop: 20, marginBottom: 40 }}
          >
            {refreshingIntegrations && <LinearProgress />}
            {!refreshingIntegrations && <div style={{ height: 4 }}></div>}
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px 16px 0 16px",
                gap: 2
              }}
            >
              <TextField
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                onClick={handleRefreshIntegrations}
                disabled={refreshingIntegrations}
              >
                <Refresh />
              </IconButton>
            </Box>
            <TableContainer data-tutorial="integration-list">
              <Table sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Integration</TableCell>
                    <TableCell align="right">Sharing</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Frequency</TableCell>
                    <TableCell align="right">Last Sync</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrationData.map((row) => {
                    const isEditable = EDITABLE_INTEGRATIONS.includes(row.type);
                    return (
                      <TableRow
                        key={row.id}
                        onClick={() => {
                          if (isEditable) {
                            setEditingIntegration(row);
                            setSelectedIntegration(row.type);
                            setDialogOpen(true);
                          }
                        }}
                        sx={{
                          cursor: isEditable ? 'pointer' : 'default',
                          '&:hover': isEditable ? {
                            backgroundColor: 'action.hover',
                          } : {},
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar
                              src={getIntegrationLogo(row)}
                              alt={row.name}
                              sx={{ width: 40, height: 40 }}
                            >
                              {row.type === 'toolset' && <Construction />}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {row.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.instanceName || "Main"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              row.sharing === INTEGRATION_SHARING_SHARED
                                ? "Visible to other team members"
                                : "Only visible to you"
                            }
                            arrow
                          >
                            <Chip
                              size="small"
                              color={
                                row.sharing === INTEGRATION_SHARING_SHARED
                                  ? "primary"
                                  : "default"
                              }
                              label={row.sharing || INTEGRATION_SHARING_PRIVATE}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            icon={getStatusIcon(row.status || "healthy")}
                            color={getStatusColor(row.status || "healthy") as any}
                            label={
                              (row.status &&
                                row.status.substring(0, 1).toUpperCase() +
                                  row.status.substring(1)) ||
                              "Healthy"
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isKnowledgeBaseIntegration(row.type) ? (
                            <Chip
                              icon={<Sync fontSize="small" />}
                              label={formatSyncFrequency(
                                row.syncFrequency || "manual"
                              )}
                              variant="outlined"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isKnowledgeBaseIntegration(row.type)
                            ? formatTimestamp(row.lastSync).fullDateTime
                            : "-"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="more"
                            aria-controls={`integration-menu-${row.type}-${row.instanceName}`}
                            aria-haspopup="true"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuOpen(event, row);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 2,
                  borderTop: "1px solid rgba(224, 224, 224, 1)",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                  Showing {integrationData.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} -{" "}
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </Typography>
              </Box>
            )}
          </Paper>
        </div>
      )}

      {/* Integration Actions Menu */}
      <Menu
        id="integration-actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedIntegrationRow &&
          EDITABLE_INTEGRATIONS.includes(selectedIntegrationRow.type) && (
            <MenuItem onClick={handleEditIntegration}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
        {selectedIntegrationRow &&
          isKnowledgeBaseIntegration(selectedIntegrationRow.type) && (
            <MenuItem onClick={handleSyncIntegration}>
              <ListItemIcon>
                <Sync fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sync</ListItemText>
            </MenuItem>
          )}
        {selectedIntegrationRow &&
         (selectedIntegrationRow.type === "browser_session" ||
          selectedIntegrationRow.type === "linkedin_login") && (
          <MenuItem onClick={handleOpenBrowserSession}>
            <ListItemIcon>
              <TravelExploreOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedIntegrationRow.type === "linkedin_login"
                ? "Relogin to LinkedIn"
                : "Open Browser Session"}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenSharingDialog}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sharing</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemoveIntegration}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>

      {/* API Key Actions Menu */}
      <Menu
        id="api-key-actions-menu"
        anchorEl={apiKeyMenuAnchorEl}
        open={Boolean(apiKeyMenuAnchorEl)}
        onClose={handleApiKeyMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleShowApiKeyFromMenu}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteApiKeyFromMenu}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <DeleteIntegrationDialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        integrationName={
          selectedIntegrationRow?.instanceName || selectedIntegrationRow?.name
        }
        loading={deletingIntegration}
      />

      <BrowserSessionDialog
        open={browserSessionDialogOpen}
        onClose={() => setBrowserSessionDialogOpen(false)}
        integration={selectedBrowserIntegration}
        companyId={companyId}
        getToken={getToken}
        onError={(error) => eventListener({ type: "SET_ERROR", error })}
        onSuccess={(message) => eventListener({ type: "SET_SUCCESS", message })}
      />

      <IntegrationSharingDialog
        open={sharingDialogOpen}
        onClose={handleSharingDialogClose}
        integration={sharingIntegration}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        onSaved={handleSharingDialogSaved}
      />

      <ToolsetsManagementDialog
        open={toolsetsDialogOpen}
        onClose={() => setToolsetsDialogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
    </Box>
  );
}
